/**MTNEvidenceVault.js — Evidence Vault & Audit Registry (telecom-specific) */
import React, { useState } from "react";
import { useMTN } from "@/context/MTNContext";
import { Upload, CheckCircle2, AlertTriangle, Archive, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import ExecutiveActionPanel from "@/components/ExecutiveActionPanel";

const EVIDENCE_TYPES = [
  { type:"tower_fuel_log", label:"Tower Fuel Logs", icon:"⛽", required:true, scope:"Scope 1", description:"Daily/weekly diesel delivery records per tower site. Required for T1 evidence classification." },
  { type:"generator_runtime", label:"Generator Runtime Logs", icon:"🔧", required:true, scope:"Scope 1", description:"Automated generator runtime data. Critical for fuel consumption verification." },
  { type:"electricity_invoice", label:"Electricity Invoices", icon:"💡", required:true, scope:"Scope 2", description:"DISCO billing data per site. Required for grid electricity baseline." },
  { type:"smart_meter", label:"Smart Meter Records", icon:"📊", required:true, scope:"Scope 1+2", description:"Sub-metered electricity data. Upgrades evidence to T1 level." },
  { type:"battery_record", label:"Battery Replacement Records", icon:"🔋", required:false, scope:"Circularity", description:"Battery lifecycle logs. Required for battery optimisation carbon credit." },
  { type:"vendor_declaration", label:"Supplier Declarations", icon:"📋", required:true, scope:"Scope 3", description:"Huawei/Ericsson/Nokia supplier emission declarations. Required for Scope 3 Cat.1." },
  { type:"dc_energy_log", label:"Data Center Energy Logs", icon:"🖥️", required:true, scope:"Scope 1+2", description:"DC-level energy consumption records. PUE measurement." },
  { type:"gps_field", label:"Field GPS Evidence", icon:"📍", required:false, scope:"Scope 1", description:"GPS-tagged field maintenance records. Supports site-level monitoring." },
  { type:"fleet_log", label:"Fleet Fuel Records", icon:"🚗", required:true, scope:"Scope 1", description:"Vehicle fuel card records and mileage logs." },
  { type:"ewaste_record", label:"E-waste Recovery Records", icon:"♻️", required:false, scope:"Circularity", description:"Certified e-waste recovery tonnage records. Required for PCR registration." },
];

export default function MTNEvidenceVault() {
  const { loading, selectedPeriod } = useMTN();
  const [activeFilter, setActiveFilter] = useState("all");
  const [uploadingType, setUploadingType] = useState(null);

  // Demo evidence status
  const evidenceStatus = {
    tower_fuel_log: { status:"Partial", count:4200, total:18500, quality:"declared", lastUpload:"2026-03-15", expiry:"2027-03-15" },
    generator_runtime: { status:"Partial", count:2800, total:18500, quality:"declared", lastUpload:"2026-04-01", expiry:"2027-04-01" },
    electricity_invoice: { status:"Complete", count:12, total:12, quality:"declared", lastUpload:"2026-04-30", expiry:"2027-04-30" },
    smart_meter: { status:"Gap", count:0, total:18500, quality:null, lastUpload:null, expiry:null },
    battery_record: { status:"Partial", count:5800, total:18500, quality:"estimated", lastUpload:"2026-02-01", expiry:"2027-02-01" },
    vendor_declaration: { status:"Partial", count:2, total:3, quality:"declared", lastUpload:"2026-01-15", expiry:"2027-01-15" },
    dc_energy_log: { status:"Complete", count:5, total:5, quality:"measured", lastUpload:"2026-04-30", expiry:"2027-04-30" },
    gps_field: { status:"Partial", count:8500, total:18500, quality:"measured", lastUpload:"2026-04-15", expiry:"2027-04-15" },
    fleet_log: { status:"Complete", count:2050, total:2050, quality:"declared", lastUpload:"2026-04-30", expiry:"2027-04-30" },
    ewaste_record: { status:"Partial", count:3, total:7, quality:"declared", lastUpload:"2026-03-01", expiry:"2027-03-01" },
  };

  const handleUpload = (type) => {
    setUploadingType(type);
    setTimeout(() => { setUploadingType(null); toast.success(`${type.replace(/_/g," ")} upload initiated`); }, 1200);
  };

  if (loading) return <div style={{padding:40,color:"#64748b",fontSize:12}}>Loading…</div>;

  const filtered = EVIDENCE_TYPES.filter(e => activeFilter==="all" || (activeFilter==="gaps"&&evidenceStatus[e.type]?.status!=="Complete") || (activeFilter==="required"&&e.required));

  const stats = {
    complete: Object.values(evidenceStatus).filter(s=>s.status==="Complete").length,
    partial: Object.values(evidenceStatus).filter(s=>s.status==="Partial").length,
    gap: Object.values(evidenceStatus).filter(s=>s.status==="Gap").length,
  };

  return (
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>Evidence Vault & Audit Registry</h1>
        <p style={{fontSize:11,color:"#475569"}}>Telecom-specific evidence governance · Immutable audit trail · Evidence quality scoring</p>
      </div>

      {/* Evidence summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
        {[["Complete",stats.complete,"#22c55e"],["Partial",stats.partial,"#f59e0b"],["Gaps",stats.gap,"#ef4444"],["Total Types",EVIDENCE_TYPES.length,"#60a5fa"]].map(([l,v,c])=>(
          <div key={l} style={{background:"#0d1423",border:`1px solid ${c}25`,borderRadius:8,padding:"12px 14px"}} data-testid={`evidence-stat-${l.toLowerCase()}`}>
            <p style={{fontSize:8,color:"#475569",textTransform:"uppercase",marginBottom:4}}>{l}</p>
            <p style={{fontSize:18,fontWeight:900,color:c}}>{v}</p>
          </div>
        ))}
      </div>

      {/* Evidence maturity progression */}
      <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16,marginBottom:14}} data-testid="evidence-maturity">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Evidence Maturity Progression — Estimated → Declared → Measured → Verified</p>
        <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:12}}>
          {[["Estimated","T3 — Engineering estimate","3 BUs","#ef4444",45],
            ["Declared","T2 — Invoice/declaration","3 BUs","#f59e0b",30],
            ["Measured","T1 — Automated meters","1 BU (DC)","#22c55e",15],
            ["Verified","VVB-verified","0 BUs","#60a5fa",0],
          ].map((s,i)=>(
            <React.Fragment key={s[0]}>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{height:12,background:s[4]>0?s[3]+"30":"#0f172a",border:`1px solid ${s[3]}40`,borderRadius:4,marginBottom:4,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <div style={{height:"100%",width:`${Math.max(s[4],4)}%`,background:s[3],borderRadius:3}}/>
                </div>
                <p style={{fontSize:8,fontWeight:700,color:s[3]}}>{s[0]}</p>
                <p style={{fontSize:7,color:"#475569"}}>{s[2]}</p>
              </div>
              {i<3 && <div style={{width:20,textAlign:"center",color:"#475569",fontSize:10}}>→</div>}
            </React.Fragment>
          ))}
        </div>
        <div style={{background:"#7f1d1d15",border:"1px solid #ef444425",borderRadius:6,padding:10}}>
          <p style={{fontSize:9,fontWeight:700,color:"#ef4444",marginBottom:4}}>Critical Missing Evidence Blocking VVB Pre-Qualification</p>
          {[["Smart meter data (T1)","Tower fuel metering at site level — prerequisite for PoA registration","CRITICAL"],
            ["POME volume baseline","Process mass balance records for distillery-equivalent processes","HIGH"],
            ["Utility sub-metering","Site-level electricity sub-meters for data center PUE calculation","HIGH"],
            ["Generator runtime logs (automated)","Automated SCADA data preferred over manual logs","HIGH"],
          ].map(([e,n,sev])=>(
            <div key={e} style={{display:"flex",gap:6,marginBottom:5}}>
              <AlertTriangle size={9} color={sev==="CRITICAL"?"#ef4444":"#f59e0b"} style={{flexShrink:0,marginTop:1}}/>
              <div>
                <span style={{fontSize:9,fontWeight:700,color:"#f1f5f9"}}>{e}</span>
                <span style={{fontSize:7,fontWeight:700,color:sev==="CRITICAL"?"#ef4444":"#f59e0b",marginLeft:6}}>{sev}</span>
                <p style={{fontSize:8,color:"#475569"}}>{n}</p>
              </div>
            </div>
          ))}
        </div>
        <ExecutiveActionPanel compact={true} title="Evidence Upgrade Recommendations" actions={[
          {severity:"Critical",category:"Evidence",title:"Deploy smart metering at top 500 diesel-heavy towers",description:"Smart meter data upgrades evidence from T3 (estimated) to T1 (measured), enabling VVB pre-qualification.",actionRequired:"Procure and install IoT fuel meters + SCADA integration",financialExposure:"Carbon market access ($450K+/yr) blocked without T1",timeline:"6 months from approval",owner:"CTO",valueUpside:"T1 evidence unlocks VVB pathway and carbon market registration"},
        ]} />
      </div>

      {/* Filter bar */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[["all","All Evidence"],["required","Required Only"],["gaps","Gaps Only"]].map(([f,l])=>(
          <button key={f} onClick={()=>setActiveFilter(f)}
            style={{padding:"5px 12px",borderRadius:5,border:"none",cursor:"pointer",fontSize:9,fontWeight:600,
              background:activeFilter===f?"#FFCB00":"#0d1423",color:activeFilter===f?"#000":"#64748b",
              border:activeFilter===f?"none":"1px solid #1e293b"}}>{l}
          </button>
        ))}
      </div>

      {/* Evidence list */}
      <div style={{display:"flex",flexDirection:"column",gap:8}} data-testid="evidence-list">
        {filtered.map(e => {
          const st = evidenceStatus[e.type] || { status:"Gap", count:0, total:1, quality:null };
          const pct = st.total > 0 ? Math.round(st.count/st.total*100) : 0;
          const statusColor = st.status==="Complete"?"#22c55e":st.status==="Partial"?"#f59e0b":"#ef4444";
          return (
            <div key={e.type} style={{background:"#0d1423",border:`1px solid ${statusColor}25`,borderRadius:8,padding:14}} data-testid={`evidence-${e.type}`}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                    <span style={{fontSize:13}}>{e.icon}</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>{e.label}</span>
                    {e.required && <span style={{fontSize:7,fontWeight:700,background:"#1e3a5f",color:"#60a5fa",padding:"1px 4px",borderRadius:3}}>REQUIRED</span>}
                    <span style={{fontSize:7,fontWeight:700,background:"#1e293b",color:"#94a3b8",padding:"1px 4px",borderRadius:3}}>{e.scope}</span>
                    <span style={{fontSize:8,fontWeight:700,padding:"1px 6px",borderRadius:3,background:statusColor+"20",color:statusColor}}>{st.status}</span>
                  </div>
                  <p style={{fontSize:9,color:"#64748b",marginBottom:6}}>{e.description}</p>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{display:"flex",gap:4,alignItems:"center"}}>
                      <div style={{width:80,height:4,background:"#1e293b",borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:statusColor,borderRadius:2}}/></div>
                      <span style={{fontSize:8,color:statusColor,fontWeight:600}}>{pct}% ({st.count.toLocaleString()}/{st.total.toLocaleString()})</span>
                    </div>
                    {st.quality && <span style={{fontSize:8,color:"#94a3b8"}}>Quality: <span style={{color:st.quality==="measured"?"#22c55e":st.quality==="declared"?"#60a5fa":"#f59e0b",fontWeight:600}}>{st.quality}</span></span>}
                    {st.lastUpload && <span style={{fontSize:8,color:"#64748b"}}>Last: {st.lastUpload}</span>}
                    {st.expiry && <span style={{fontSize:8,color:"#64748b"}}>Expires: {st.expiry}</span>}
                  </div>
                  {st.status==="Gap" && (
                    <div style={{display:"flex",gap:5,marginTop:6,padding:"5px 8px",background:"#7f1d1d20",borderRadius:5}}>
                      <AlertTriangle size={9} color="#ef4444" style={{flexShrink:0,marginTop:1}}/>
                      <p style={{fontSize:8,color:"#f87171"}}>Missing evidence — blocks T1 classification and VVB pre-qualification. Priority: deploy smart metering programme.</p>
                    </div>
                  )}
                </div>
                <button onClick={()=>handleUpload(e.type)} disabled={uploadingType===e.type}
                  style={{marginLeft:12,background:"#FFCB00",color:"#000",border:"none",borderRadius:5,padding:"5px 10px",fontSize:8,fontWeight:700,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",gap:4}}
                  data-testid={`upload-${e.type}`}>
                  {uploadingType===e.type ? "Uploading…" : <><Upload size={9}/> Upload</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
